import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Copy, Check, ArrowRight, ArrowLeft } from 'lucide-react';

const RouterStep1Script = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  const router = location.state?.router || location.state?.pendingRouter;
  const [copied, setCopied] = useState(false);

  if (!router) {
    navigate('/routers');
    return null;
  }

  // Generar script completo
  const getTimezone = (countryCode) => {
    const timezones = {
      'MX': 'America/Mexico_City',
      'GT': 'America/Guatemala',
      'HN': 'America/Tegucigalpa',
      'SV': 'America/El_Salvador',
      'NI': 'America/Managua',
      'CR': 'America/Costa_Rica',
      'PA': 'America/Panama',
      'CO': 'America/Bogota',
      'VE': 'America/Caracas',
      'EC': 'America/Guayaquil',
      'PE': 'America/Lima',
      'BO': 'America/La_Paz',
      'CL': 'America/Santiago',
      'AR': 'America/Argentina/Buenos_Aires',
      'UY': 'America/Montevideo',
      'PY': 'America/Asuncion',
      'BR': 'America/Sao_Paulo',
    };
    return timezones[countryCode] || 'America/Mexico_City';
  };

  const timezone = getTimezone(user.country_code || 'MX');
  
  const script = `# Limpiar configuración anterior si existe
/interface l2tp-client remove [find name="VPN-ETWEB"]
/user remove [find name="ETWEB"]
/ip route remove [find comment="Ruta para EasyTickets"]

# Crear nueva configuración VPN
/interface l2tp-client add connect-to=45.32.207.196 disabled=no name="VPN-ETWEB" password=${router.vpn_password} user=${router.vpn_username}
/user add disable=no group=full name=ETWEB password=${router.vpn_password}

# Habilitar servicios API y WinBox
/ip service enable api
/ip service set api port=8728
/ip service enable winbox
/ip service set winbox port=8291

# Configurar NTP (sincronización de hora)
/system ntp client set enabled=yes primary-ntp=192.36.143.130 secondary-ntp=200.23.163.50

# Configurar zona horaria según país
/system clock set time-zone-name=${timezone}`;

  const handleCopy = () => {
    // Usar método alternativo porque clipboard API requiere HTTPS
    const textarea = document.createElement('textarea');
    textarea.value = script;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('Error al copiar. Por favor, copia el script manualmente.');
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const handleNext = () => {
    navigate('/router-step3-connect', { 
      state: { 
        router: router
      } 
    });
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={() => navigate('/routers')}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              📡 Configurar Router #{router.router_index}
            </h1>
          </div>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} ml-14`}>
            Paso 1 de 2: Copiar Script de Configuración
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-blue-600 rounded-full"></div>
            <div className={`flex-1 h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded-full`}></div>
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span className="text-blue-600 font-semibold">Script</span>
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Conectar</span>
          </div>
        </div>

        {/* Credenciales */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            🔑 Credenciales VPN del Router
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Usuario VPN:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.vpn_username}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Contraseña:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.vpn_password}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>IP Interna VPN:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{router.internal_ip || router.vpn_internal_ip}</p>
            </div>
            <div>
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Servidor VPN:</span>
              <p className={`font-mono font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>45.32.207.196</p>
            </div>
          </div>
        </div>

        {/* Script */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              📜 Script de Configuración
            </h3>
            <button
              onClick={handleCopy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold ${
                copied 
                  ? 'bg-green-600 text-white' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copiado
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copiar Script
                </>
              )}
            </button>
          </div>
          
          <div className={`${isDark ? 'bg-gray-900' : 'bg-gray-50'} rounded-lg p-4 overflow-x-auto`}>
            <pre className={`text-sm ${isDark ? 'text-green-400' : 'text-gray-800'} font-mono`}>
              {script}
            </pre>
          </div>
        </div>

        {/* Instrucciones */}
        <div className={`${isDark ? 'bg-blue-900/20 border-blue-500/30' : 'bg-blue-50 border-blue-200'} border rounded-xl p-6 mb-6`}>
          <h4 className={`font-semibold mb-3 ${isDark ? 'text-blue-400' : 'text-blue-900'}`}>
            📋 Instrucciones
          </h4>
          <ol className={`space-y-2 text-sm ${isDark ? 'text-blue-200' : 'text-blue-800'}`}>
            <li>1. Haz clic en "Copiar Script"</li>
            <li>2. Abre WinBox y conéctate a tu MikroTik</li>
            <li>3. Ve a la terminal (menú New Terminal)</li>
            <li>4. Pega el script completo</li>
            <li>5. Presiona Enter para ejecutar</li>
            <li>6. Haz clic en "Siguiente" cuando termines</li>
          </ol>
        </div>

        {/* Botones */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/routers')}
            className={`px-6 py-3 rounded-lg font-semibold ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <ArrowLeft className="w-5 h-5 inline mr-2" />
            Cancelar
          </button>
          <button
            onClick={handleNext}
            className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 flex items-center justify-center gap-2"
          >
            Siguiente: Conectar Router
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouterStep1Script;
